import { Suspense, lazy } from "react";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AppQueryClientProvider } from "@/components/AppQueryClientProvider";
import { SentryUserContext } from "@/components/SentryUserContext";
import { ErrorBoundary, withSentryReactRouterV7Routing } from "@sentry/react";
import { BrowserRouter, Routes, Route, Outlet } from "react-router-dom";
import { NuqsAdapter } from "nuqs/adapters/react-router/v7";
import { AuthProvider } from "@/contexts/AuthContext";
import { AuthGuard } from "@/components/auth/AuthGuard";
import { GuestGuard } from "@/components/auth/GuestGuard";
import { RestaurantGuard } from "@/components/auth/RestaurantGuard";
import { RoleGuard } from "@/components/auth/RoleGuard";
import { PinGate } from "@/components/auth/PinGate";
import { GlobalErrorFallback } from "@/components/error/GlobalErrorFallback";
import { Spinner } from "./components/ui/spinner";
import { AdminLayout } from "./components/admin/AdminLayout";
import { OrderingLayout } from "./components/ordering/OrderingLayout";
import { SlugLayout } from "./components/public/SlugLayout";

const SignIn = lazy(() => import("./pages/SignIn"));
const Signup = lazy(() => import("./pages/Signup"));
const AuthCallback = lazy(() => import("./pages/AuthCallback"));
const UpdatePassword = lazy(() => import("./pages/UpdatePassword"));
const Onboarding = lazy(() => import("./pages/Onboarding"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Orders = lazy(() => import("./pages/Orders"));
const Kitchen = lazy(() => import("./pages/Kitchen"));
const MenuManagement = lazy(() => import("./pages/MenuManagement"));
const Analytics = lazy(() => import("./pages/Analytics"));
const StaffManagement = lazy(() => import("./pages/StaffManagement"));
const Settings = lazy(() => import("./pages/Settings"));
const Automations = lazy(() => import("./pages/Automations"));
const TableManagement = lazy(() => import("./pages/TableManagement"));
const Integrations = lazy(() => import("./pages/Integrations"));
const Reports = lazy(() => import("./pages/Reports"));
const ZReport = lazy(() => import("./pages/ZReport"));
const PeriodReport = lazy(() => import("./pages/PeriodReport"));
const CustomersManagement = lazy(() => import("./pages/CustomersManagement"));
const Invoices = lazy(() => import("./pages/Invoices"));
const Terminal = lazy(() => import("./pages/Terminal"));
const TerminalSettings = lazy(() => import("./pages/TerminalSettings"));
const LunchSchedule = lazy(() => import("./pages/LunchSchedule"));
const Reservations = lazy(() => import("./pages/Reservations"));
const BookingsOverview = lazy(() => import("./pages/BookingsOverview"));
const ReservationSettings = lazy(() => import("./pages/ReservationSettings"));
const EmailCampaigns = lazy(() => import("./pages/EmailCampaigns"));
const FramerSync = lazy(() => import("./pages/FramerSync"));
const MarketingAnalytics = lazy(() => import("./pages/MarketingAnalytics"));
const SocialMediaPlanner = lazy(() => import("./pages/SocialMediaPlanner"));
const LoyaltyMembers = lazy(() => import("./pages/LoyaltyMembers"));
const LoyaltySettings = lazy(() => import("./pages/LoyaltySettings"));
const LoyaltyRewards = lazy(() => import("./pages/LoyaltyRewards"));
const LoyaltyVouchers = lazy(() => import("./pages/LoyaltyVouchers"));
const LoyaltyTopUp = lazy(() => import("./pages/LoyaltyTopUp"));
const LoyaltyPush = lazy(() => import("./pages/LoyaltyPush"));
const LoyaltyRedemptions = lazy(() => import("./pages/LoyaltyRedemptions"));
const StaffSchedule = lazy(() => import("./pages/StaffSchedule"));
const StaffTimeClock = lazy(() => import("./pages/StaffTimeClock"));
const StaffOverview = lazy(() => import("./pages/StaffOverview"));
const StaffLeave = lazy(() => import("./pages/StaffLeave"));

const OrderTypesManagement = lazy(() => import("./pages/OrderTypesManagement"));
const ProfileManagement = lazy(() => import("./pages/ProfileManagement"));
const TimestampMenu = lazy(() => import("./pages/TimestampMenu"));
const MenuStyle = lazy(() => import("./pages/MenuStyle"));
const LunchMenuDesigner = lazy(() => import("./pages/LunchMenuDesigner"));
const SignageDevices = lazy(() => import("./pages/SignageDevices"));
const SignageGroups = lazy(() => import("./pages/SignageGroups"));
const SignageAssets = lazy(() => import("./pages/SignageAssets"));
const SignagePlaylists = lazy(() => import("./pages/SignagePlaylists"));
const SignageSchedules = lazy(() => import("./pages/SignageSchedules"));
const SignageZones = lazy(() => import("./pages/SignageZones"));
const SignageAlerts = lazy(() => import("./pages/SignageAlerts"));
const SignageAnalytics = lazy(() => import("./pages/SignageAnalytics"));
const ServiceManagement = lazy(() => import("./pages/ServiceManagement"));
const ServiceResources = lazy(() => import("./pages/ServiceResources"));
const ServiceCalendar = lazy(() => import("./pages/ServiceCalendar"));
const ServiceBookings = lazy(() => import("./pages/ServiceBookings"));
const ServiceSettings = lazy(() => import("./pages/ServiceSettings"));
const AIDashboard = lazy(() => import("./pages/AIDashboard"));
const AIPriceIntelligence = lazy(() => import("./pages/AIPriceIntelligence"));
const AITrendAnalysis = lazy(() => import("./pages/AITrendAnalysis"));
const AIMenuOptimizer = lazy(() => import("./pages/AIMenuOptimizer"));
const AIRecipeGenerator = lazy(() => import("./pages/AIRecipeGenerator"));
const AIHistory = lazy(() => import("./pages/AIHistory"));
const AIWasteAnalysis = lazy(() => import("./pages/AIWasteAnalysis"));
const AIStaffOptimizer = lazy(() => import("./pages/AIStaffOptimizer"));
const AICustomerInsights = lazy(() => import("./pages/AICustomerInsights"));
const Inventory = lazy(() => import("./pages/Inventory"));
const ArticleImport = lazy(() => import("./pages/ArticleImport"));
const ProjectsPage = lazy(() => import("./pages/ProjectsPage"));
const TodoBoard = lazy(() => import("./pages/TodoBoard"));
const QuotesPage = lazy(() => import("./pages/QuotesPage"));
const QuoteBuilder = lazy(() => import("./pages/QuoteBuilder"));
const SupplierManagement = lazy(() => import("./pages/SupplierManagement"));
const EventsManagement = lazy(() => import("./pages/EventsManagement"));
const CustomerReviews = lazy(() => import("./pages/CustomerReviews"));
const GDPRTools = lazy(() => import("./pages/GDPRTools"));
const LocationManagement = lazy(() => import("./pages/LocationManagement"));
const RoleManagement = lazy(() => import("./pages/RoleManagement"));
const DeliveryTracking = lazy(() => import("./pages/DeliveryTracking"));
const HappyHourManagement = lazy(() => import("./pages/HappyHourManagement"));
const OfflineSettings = lazy(() => import("./pages/OfflineSettings"));
const TagsManagement = lazy(() => import("./pages/TagsManagement"));
const ComminovaNet = lazy(() => import("./pages/ComminovaNet"));
const ExpressCheckout = lazy(() => import("./pages/ExpressCheckout"));
/** POS second screen: mirrors `terminal_cart` from localStorage (separate window). */
const AdminCustomerCartDisplay = lazy(
  () => import("./components/terminal/CustomerDisplayView")
);
/** Public / in-venue order status board (kitchen-facing queue). */
const CustomerDisplay = lazy(() => import("./pages/CustomerDisplay"));
const CateringInquiry = lazy(() => import("./pages/CateringInquiry"));
const QuoteSigningPage = lazy(() => import("./pages/QuoteSigningPage"));
const LandingPage = lazy(() => import("./pages/LandingPage"));
const Industries = lazy(() => import("./pages/landing/Industries"));
const Features = lazy(() => import("./pages/landing/Features"));
const Pricing = lazy(() => import("./pages/landing/Pricing"));
const PosSystem = lazy(() => import("./pages/products/PosSystem"));
const OnlineOrdering = lazy(() => import("./pages/products/OnlineOrdering"));
const KitchenDisplay = lazy(() => import("./pages/products/KitchenDisplay"));
const TableReservations = lazy(
  () => import("./pages/products/TableReservations")
);
const ServiceBooking = lazy(() => import("./pages/products/ServiceBooking"));
const IntegrationsInfo = lazy(
  () => import("./pages/products/IntegrationsInfo")
);
const ExpressCheckoutProduct = lazy(
  () => import("./pages/products/ExpressCheckout")
);
const QrOrdering = lazy(() => import("./pages/products/QrOrdering"));
const StaffManagementProduct = lazy(
  () => import("./pages/products/StaffManagement")
);
const LoyaltyProgram = lazy(() => import("./pages/products/LoyaltyProgram"));
const MarketingProductPage = lazy(() => import("./pages/products/Marketing"));
const AITools = lazy(() => import("./pages/products/AITools"));
const EventCatering = lazy(() => import("./pages/products/EventCatering"));
const SuppliersProduct = lazy(() => import("./pages/products/Suppliers"));
const ComminovaNetProduct = lazy(() => import("./pages/products/ComminovaNet"));
const GiftCards = lazy(() => import("./pages/products/GiftCards"));
const QuoteBuilderProduct = lazy(() => import("./pages/products/QuoteBuilder"));
const DigitalSignage = lazy(() => import("./pages/products/DigitalSignage"));
const ProjectManagementProduct = lazy(
  () => import("./pages/products/ProjectManagement")
);
const InvoicingProduct = lazy(() => import("./pages/products/Invoicing"));
const GdprToolsProduct = lazy(() => import("./pages/products/GdprTools"));
const CustomerReviewsProduct = lazy(
  () => import("./pages/products/CustomerReviews")
);
const HappyHourProduct = lazy(() => import("./pages/products/HappyHour"));
const CateringInquiryProduct = lazy(
  () => import("./pages/products/CateringInquiry")
);
const LandingLayout = lazy(() =>
  import("./components/landing/LandingLayout").then((m) => ({
    default: m.LandingLayout,
  }))
);
const Order = lazy(() => import("./pages/Order"));
const OrderStatus = lazy(() => import("./pages/OrderStatus"));
const OrderPublicPay = lazy(() => import("./pages/OrderPublicPay"));
const OrderPaymentComplete = lazy(() => import("./pages/OrderPaymentComplete"));
const OrderPaymentCancel = lazy(() => import("./pages/OrderPaymentCancel"));
const BookingPage = lazy(() => import("./pages/BookingPage"));
const SignagePlayer = lazy(() => import("./pages/SignagePlayer"));
const GiftCardPage = lazy(() => import("./pages/GiftCardPage"));
const ServiceBookingPage = lazy(() => import("./pages/ServiceBookingPage"));
const TableStatus = lazy(() => import("./pages/TableStatus"));
const NotFound = lazy(() => import("./pages/NotFound"));
const ReactQueryDevtools = import.meta.env.DEV
  ? lazy(() =>
      import("@tanstack/react-query-devtools").then((m) => ({
        default: m.ReactQueryDevtools,
      }))
    )
  : null;

const OnDemandDashboard = lazy(() => import("./pages/OnDemandDashboard"));
const OnDemandCredits = lazy(() => import("./pages/OnDemandCredits"));
const OnDemandCatalog = lazy(() => import("./pages/OnDemandCatalog"));
const OnDemandNewRequest = lazy(() => import("./pages/OnDemandNewRequest"));
const OnDemandRequests = lazy(() => import("./pages/OnDemandRequests"));
const OnDemandRequestDetail = lazy(
  () => import("./pages/OnDemandRequestDetail")
);
const OnDemandProjects = lazy(() => import("./pages/OnDemandProjects"));
const OnDemandDeliverables = lazy(() => import("./pages/OnDemandDeliverables"));
const OnDemandSettings = lazy(() => import("./pages/OnDemandSettings"));
const OnDemandOps = lazy(() => import("./pages/OnDemandOps"));
const OnDemandCalendar = lazy(() => import("./pages/OnDemandCalendar"));
const OnDemandBrands = lazy(() => import("./pages/OnDemandBrands"));
const Inbox = lazy(() => import("./pages/Inbox"));
const InboxStandups = lazy(() => import("./pages/InboxStandups"));
const InboxAnalytics = lazy(() => import("./pages/InboxAnalytics"));
const InboxDecisions = lazy(() => import("./pages/InboxDecisions"));

const SentryRoutes = withSentryReactRouterV7Routing(Routes);

const Loader = () => (
  <div className="text-center flex items-center justify-center h-full w-full">
    <Spinner className="text-muted-foreground" />
    <span className="sr-only">Loading</span>
  </div>
);

/** Single boundary for lazy `element`s below this layout route's `<Outlet />` (no need to wrap each child route). */
function LazySuspenseOutlet() {
  return (
    <Suspense fallback={<Loader />}>
      <Outlet />
    </Suspense>
  );
}

const App = () => (
  <AppQueryClientProvider>
    <TooltipProvider>
      <AuthProvider>
        <BrowserRouter>
          <NuqsAdapter>
            <Sonner />
            <SentryUserContext />
            <ErrorBoundary
              fallback={({ error }) => <GlobalErrorFallback error={error} />}
            >
              <SentryRoutes>
                <Route element={<LazySuspenseOutlet />}>
                  <Route path="/order/:slug?" element={<SlugLayout />}>
                    <Route element={<OrderingLayout />}>
                      <Route index element={<Order />} />
                      <Route path="pay/:orderId" element={<OrderPublicPay />} />
                      <Route
                        path="payment/complete"
                        element={<OrderPaymentComplete />}
                      />
                      <Route
                        path="payment/cancel"
                        element={<OrderPaymentCancel />}
                      />
                      <Route
                        path="status/:orderNumber"
                        element={<OrderStatus />}
                      />
                    </Route>
                  </Route>

                  {/* Public booking – SlugLayout; /order adds OrderingLayout inside SlugLayout */}
                  <Route path="/book/:slug?" element={<SlugLayout />}>
                    <Route index element={<BookingPage />} />
                  </Route>

                  {/* Public signage player (fullscreen) */}
                  <Route path="/signage/player" element={<SignagePlayer />} />

                  <Route path="/gift/:slug/:code" element={<SlugLayout />}>
                    <Route index element={<GiftCardPage />} />
                  </Route>

                  <Route path="/services/:slug?" element={<SlugLayout />}>
                    <Route index element={<ServiceBookingPage />} />
                  </Route>

                  <Route
                    path="/table-status/:slug/:reservationId"
                    element={<SlugLayout />}
                  >
                    <Route index element={<TableStatus />} />
                  </Route>

                  <Route path="/display/:slug?" element={<SlugLayout />}>
                    <Route index element={<CustomerDisplay />} />
                  </Route>
                  <Route
                    path="/catering-inquiry/:slug?"
                    element={<SlugLayout />}
                  >
                    <Route index element={<CateringInquiry />} />
                  </Route>

                  <Route path="/sign" element={<QuoteSigningPage />} />
                </Route>

                <Route
                  element={
                    <GuestGuard>
                      <LazySuspenseOutlet />
                    </GuestGuard>
                  }
                >
                  <Route path="/" element={<SignIn />} />
                  <Route path="/signin" element={<SignIn />} />
                  <Route path="/signup" element={<Signup />} />
                </Route>

                <Route element={<LazySuspenseOutlet />}>
                  <Route path="/auth/callback" element={<AuthCallback />} />
                  <Route
                    path="/auth/update-password"
                    element={<UpdatePassword />}
                  />
                </Route>

                <Route element={<AuthGuard />}>
                  <Route path="/admin/onboarding" element={<Onboarding />} />
                  <Route element={<RestaurantGuard />}>
                    <Route element={<RoleGuard />}>
                      <Route element={<AdminLayout />}>
                        <Route path="/admin" element={<Dashboard />} />
                        <Route
                          path="/admin/menu"
                          element={<MenuManagement />}
                        />
                        <Route
                          path="/admin/order-types"
                          element={<OrderTypesManagement />}
                        />
                        <Route
                          path="/admin/integrations"
                          element={<Integrations />}
                        />
                        <Route
                          path="/admin/analytics"
                          element={<Analytics />}
                        />
                        <Route
                          path="/admin/customers"
                          element={<CustomersManagement />}
                        />
                        <Route path="/admin/invoices" element={<Invoices />} />
                        <Route
                          path="/admin/staff"
                          element={<StaffManagement />}
                        />
                        <Route
                          path="/admin/notification-templates"
                          element={<Automations />}
                        />
                        <Route path="/admin/settings" element={<Settings />} />
                        <Route
                          path="/admin/terminal-settings"
                          element={<TerminalSettings />}
                        />
                        <Route path="/admin/reports" element={<Reports />} />
                        <Route
                          path="/admin/reports/z-report"
                          element={<ZReport />}
                        />
                        <Route
                          path="/admin/reports/period"
                          element={<PeriodReport />}
                        />
                        <Route
                          path="/admin/lunch-schedule"
                          element={<LunchSchedule />}
                        />
                        <Route
                          path="/admin/timestamp-menu"
                          element={<TimestampMenu />}
                        />
                        <Route
                          path="/admin/menu-style"
                          element={<MenuStyle />}
                        />
                        <Route
                          path="/admin/menu-designer"
                          element={<LunchMenuDesigner />}
                        />
                        <Route
                          path="/admin/bookings-overview"
                          element={<BookingsOverview />}
                        />
                        <Route
                          path="/admin/reservations"
                          element={<Reservations />}
                        />
                        <Route
                          path="/admin/reservation-settings"
                          element={<ReservationSettings />}
                        />
                        <Route
                          path="/admin/email-campaigns"
                          element={<EmailCampaigns />}
                        />
                        <Route
                          path="/admin/marketing"
                          element={<MarketingAnalytics />}
                        />
                        <Route
                          path="/admin/social-media"
                          element={<SocialMediaPlanner />}
                        />
                        <Route
                          path="/admin/loyalty/members"
                          element={<LoyaltyMembers />}
                        />
                        <Route
                          path="/admin/loyalty/settings"
                          element={<LoyaltySettings />}
                        />
                        <Route
                          path="/admin/loyalty/rewards"
                          element={<LoyaltyRewards />}
                        />
                        <Route
                          path="/admin/loyalty/vouchers"
                          element={<LoyaltyVouchers />}
                        />
                        <Route
                          path="/admin/loyalty/topup"
                          element={<LoyaltyTopUp />}
                        />
                        <Route
                          path="/admin/loyalty/push"
                          element={<LoyaltyPush />}
                        />
                        <Route
                          path="/admin/loyalty/redemptions"
                          element={<LoyaltyRedemptions />}
                        />
                        <Route
                          path="/admin/framer-sync"
                          element={<FramerSync />}
                        />
                        <Route
                          path="/admin/staff/schedule"
                          element={<StaffSchedule />}
                        />
                        <Route
                          path="/admin/staff/overview"
                          element={<StaffOverview />}
                        />
                        <Route
                          path="/admin/staff/leave"
                          element={<StaffLeave />}
                        />
                        <Route
                          path="/admin/signage/devices"
                          element={<SignageDevices />}
                        />
                        <Route
                          path="/admin/signage/groups"
                          element={<SignageGroups />}
                        />
                        <Route
                          path="/admin/signage/assets"
                          element={<SignageAssets />}
                        />
                        <Route
                          path="/admin/signage/playlists"
                          element={<SignagePlaylists />}
                        />
                        <Route
                          path="/admin/signage/schedules"
                          element={<SignageSchedules />}
                        />
                        <Route
                          path="/admin/signage/zones"
                          element={<SignageZones />}
                        />
                        <Route
                          path="/admin/signage/alerts"
                          element={<SignageAlerts />}
                        />
                        <Route
                          path="/admin/signage/analytics"
                          element={<SignageAnalytics />}
                        />
                        <Route
                          path="/admin/services"
                          element={<ServiceManagement />}
                        />
                        <Route
                          path="/admin/services/resources"
                          element={<ServiceResources />}
                        />
                        <Route
                          path="/admin/services/calendar"
                          element={<ServiceCalendar />}
                        />
                        <Route
                          path="/admin/services/bookings"
                          element={<ServiceBookings />}
                        />
                        <Route
                          path="/admin/services/settings"
                          element={<ServiceSettings />}
                        />
                        <Route
                          path="/admin/ai/dashboard"
                          element={<AIDashboard />}
                        />
                        <Route
                          path="/admin/ai/price-intelligence"
                          element={<AIPriceIntelligence />}
                        />
                        <Route
                          path="/admin/ai/trends"
                          element={<AITrendAnalysis />}
                        />
                        <Route
                          path="/admin/ai/menu-optimizer"
                          element={<AIMenuOptimizer />}
                        />
                        <Route
                          path="/admin/ai/recipes"
                          element={<AIRecipeGenerator />}
                        />
                        <Route
                          path="/admin/ai/waste"
                          element={<AIWasteAnalysis />}
                        />
                        <Route
                          path="/admin/ai/staff-optimizer"
                          element={<AIStaffOptimizer />}
                        />
                        <Route
                          path="/admin/ai/customer-insights"
                          element={<AICustomerInsights />}
                        />
                        <Route
                          path="/admin/ai/history"
                          element={<AIHistory />}
                        />
                        <Route
                          path="/admin/inventory"
                          element={<Inventory />}
                        />
                        <Route
                          path="/admin/article-import"
                          element={<ArticleImport />}
                        />
                        <Route
                          path="/admin/projects"
                          element={<ProjectsPage />}
                        />
                        <Route path="/admin/todo" element={<TodoBoard />} />
                        <Route
                          path="/admin/on-demand"
                          element={<OnDemandDashboard />}
                        />
                        <Route
                          path="/admin/on-demand/credits"
                          element={<OnDemandCredits />}
                        />
                        <Route
                          path="/admin/on-demand/catalog"
                          element={<OnDemandCatalog />}
                        />
                        <Route
                          path="/admin/on-demand/new-request"
                          element={<OnDemandNewRequest />}
                        />
                        <Route
                          path="/admin/on-demand/requests"
                          element={<OnDemandRequests />}
                        />
                        <Route
                          path="/admin/on-demand/requests/:requestId"
                          element={<OnDemandRequestDetail />}
                        />
                        <Route
                          path="/admin/on-demand/projects"
                          element={<OnDemandProjects />}
                        />
                        <Route
                          path="/admin/on-demand/deliverables"
                          element={<OnDemandDeliverables />}
                        />
                        <Route
                          path="/admin/on-demand/settings"
                          element={<OnDemandSettings />}
                        />
                        <Route
                          path="/admin/on-demand/ops"
                          element={<OnDemandOps />}
                        />
                        <Route
                          path="/admin/on-demand/calendar"
                          element={<OnDemandCalendar />}
                        />
                        <Route
                          path="/admin/on-demand/brands"
                          element={<OnDemandBrands />}
                        />
                        <Route path="/admin/inbox" element={<Inbox />} />
                        <Route
                          path="/admin/inbox/standups"
                          element={<InboxStandups />}
                        />
                        <Route
                          path="/admin/inbox/team-reports"
                          element={<InboxStandups />}
                        />
                        <Route
                          path="/admin/inbox/analytics"
                          element={<InboxAnalytics />}
                        />
                        <Route
                          path="/admin/inbox/decisions"
                          element={<InboxDecisions />}
                        />
                        <Route path="/admin/quotes" element={<QuotesPage />} />
                        <Route
                          path="/admin/quotes/:quoteId"
                          element={<QuoteBuilder />}
                        />
                        <Route
                          path="/admin/suppliers"
                          element={<SupplierManagement />}
                        />
                        <Route
                          path="/admin/events"
                          element={<EventsManagement />}
                        />
                        <Route
                          path="/admin/reviews"
                          element={<CustomerReviews />}
                        />
                        <Route path="/admin/gdpr" element={<GDPRTools />} />
                        <Route
                          path="/admin/locations"
                          element={<LocationManagement />}
                        />
                        <Route
                          path="/admin/roles"
                          element={<RoleManagement />}
                        />
                        <Route
                          path="/admin/delivery-tracking"
                          element={<DeliveryTracking />}
                        />
                        <Route
                          path="/admin/happy-hour"
                          element={<HappyHourManagement />}
                        />
                        <Route
                          path="/admin/offline"
                          element={<OfflineSettings />}
                        />
                        <Route
                          path="/admin/tags"
                          element={<TagsManagement />}
                        />
                        <Route path="/admin/net" element={<ComminovaNet />} />

                        <Route path="/admin/orders" element={<Orders />} />
                        <Route
                          path="/admin/tables"
                          element={<TableManagement />}
                        />
                        <Route
                          path="/admin/profile"
                          element={<ProfileManagement />}
                        />
                        <Route
                          path="/admin/staff/timeclock"
                          element={<StaffTimeClock />}
                        />
                      </Route>

                      {/* Fullscreen / POS — no AdminLayout; same RoleGuard + ROUTE_PERMISSIONS */}
                      <Route element={<PinGate />}>
                        <Route
                          path="/admin/express-checkout"
                          element={<ExpressCheckout />}
                        />
                        <Route path="/admin/kitchen" element={<Kitchen />} />
                        <Route path="/admin/terminal" element={<Terminal />} />
                      </Route>
                      <Route
                        path="/admin/customer-display"
                        element={<AdminCustomerCartDisplay />}
                      />
                    </Route>
                  </Route>
                </Route>

                {/* Landing & product pages (English slugs; see LANDING_ROUTES) */}
                <Route element={<LandingLayout />}>
                  <Route path="/demopage" element={<LandingPage />} />
                  <Route path="/industries" element={<Industries />} />
                  <Route path="/features" element={<Features />} />
                  <Route path="/pricing" element={<Pricing />} />
                  <Route path="/products/pos-system" element={<PosSystem />} />
                  <Route
                    path="/products/online-ordering"
                    element={<OnlineOrdering />}
                  />
                  <Route
                    path="/products/kitchen-display"
                    element={<KitchenDisplay />}
                  />
                  <Route
                    path="/products/table-reservations"
                    element={<TableReservations />}
                  />
                  <Route
                    path="/products/service-booking"
                    element={<ServiceBooking />}
                  />
                  <Route
                    path="/products/integrations"
                    element={<IntegrationsInfo />}
                  />
                  <Route
                    path="/products/express-checkout"
                    element={<ExpressCheckoutProduct />}
                  />
                  <Route
                    path="/products/qr-ordering"
                    element={<QrOrdering />}
                  />
                  <Route
                    path="/products/staff-management"
                    element={<StaffManagementProduct />}
                  />
                  <Route
                    path="/products/loyalty-program"
                    element={<LoyaltyProgram />}
                  />
                  <Route
                    path="/products/marketing"
                    element={<MarketingProductPage />}
                  />
                  <Route path="/products/ai-tools" element={<AITools />} />
                  <Route
                    path="/products/event-catering"
                    element={<EventCatering />}
                  />
                  <Route
                    path="/products/suppliers"
                    element={<SuppliersProduct />}
                  />
                  <Route
                    path="/products/comminova-net"
                    element={<ComminovaNetProduct />}
                  />
                  <Route path="/products/gift-cards" element={<GiftCards />} />
                  <Route
                    path="/products/quote-builder"
                    element={<QuoteBuilderProduct />}
                  />
                  <Route
                    path="/products/digital-signage"
                    element={<DigitalSignage />}
                  />
                  <Route
                    path="/products/project-management"
                    element={<ProjectManagementProduct />}
                  />
                  <Route
                    path="/products/invoicing"
                    element={<InvoicingProduct />}
                  />
                  <Route
                    path="/products/gdpr-tools"
                    element={<GdprToolsProduct />}
                  />
                  <Route
                    path="/products/customer-reviews"
                    element={<CustomerReviewsProduct />}
                  />
                  <Route
                    path="/products/happy-hour"
                    element={<HappyHourProduct />}
                  />
                  <Route
                    path="/products/catering-inquiry"
                    element={<CateringInquiryProduct />}
                  />
                </Route>

                {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                <Route
                  path="*"
                  element={
                    <Suspense fallback={<Loader />}>
                      <NotFound />
                    </Suspense>
                  }
                />
              </SentryRoutes>
            </ErrorBoundary>
          </NuqsAdapter>
        </BrowserRouter>
      </AuthProvider>
    </TooltipProvider>
    {ReactQueryDevtools ? (
      <Suspense fallback={null}>
        <ReactQueryDevtools
          initialIsOpen={false}
          buttonPosition="bottom-right"
        />
      </Suspense>
    ) : null}
  </AppQueryClientProvider>
);

export default App;
